/**
 * Import Progress Component
 * 
 * Shows real-time import progress with cancellation capability
 * Displays current processing status and row-by-row progress
 */

import React from 'react';
import { Loader2, X, Clock, CheckCircle } from 'lucide-react';

const ImportProgress = ({ 
  progress, 
  onCancel, 
  disabled = false 
}) => {
  const {
    currentRow = 0,
    totalRows = 0,
    processedRows = 0,
    successfulRows = 0,
    failedRows = 0,
    currentAssetName = '',
    status = 'processing',
    estimatedTimeRemaining = null,
    processingRate = null
  } = progress;

  const progressPercentage = totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : 0;
  const isComplete = status === 'completed' || status === 'cancelled';

  const formatTime = (seconds) => {
    if (!seconds || seconds < 0) return '';
    
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case 'cancelled':
        return <X className="h-6 w-6 text-red-500" />;
      default:
        return <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />;
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'processing':
        return currentAssetName 
          ? `Processing: ${currentAssetName}` 
          : 'Processing asset data...';
      case 'completed':
        return 'Import completed successfully!';
      case 'cancelled':
        return 'Import was cancelled by user';
      default:
        return 'Processing...';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'processing':
        return 'text-blue-600';
      case 'completed':
        return 'text-green-600';
      case 'cancelled':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h3 className="text-lg font-medium text-gray-900">Import in Progress</h3>
              <p className={`text-sm ${getStatusColor()}`}>{getStatusMessage()}</p>
            </div>
          </div>
          
          {!isComplete && (
            <button
              onClick={onCancel}
              disabled={disabled}
              className="flex items-center space-x-2 px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="h-4 w-4" />
              <span>Cancel Import</span>
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Progress: {processedRows} of {totalRows} rows</span>
            <span>{progressPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${
                status === 'completed' ? 'bg-green-500' : 
                status === 'cancelled' ? 'bg-red-500' : 'bg-blue-500'
              }`}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Progress Statistics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded border">
            <div className="text-2xl font-bold text-blue-600">{processedRows}</div>
            <div className="text-sm text-gray-600">Processed</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded border border-green-200">
            <div className="text-2xl font-bold text-green-600">{successfulRows}</div>
            <div className="text-sm text-gray-600">Successful</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded border border-red-200">
            <div className="text-2xl font-bold text-red-600">{failedRows}</div>
            <div className="text-sm text-gray-600">Failed</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded border">
            <div className="text-2xl font-bold text-gray-600">{totalRows - processedRows}</div>
            <div className="text-sm text-gray-600">Remaining</div>
          </div>
        </div>

        {/* Processing Details */}
        {!isComplete && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {estimatedTimeRemaining && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>Est. time remaining: {formatTime(estimatedTimeRemaining)}</span>
              </div>
            )}
            {processingRate && (
              <div className="text-sm text-gray-600">
                Processing rate: {Math.round(processingRate)} rows/second
              </div>
            )}
          </div>
        )}

        {/* Current Processing Status */}
        {!isComplete && currentAssetName && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <div className="text-sm">
              <span className="font-medium text-blue-800">Currently processing:</span>
              <span className="text-blue-700 ml-2">{currentAssetName}</span>
            </div>
            <div className="text-xs text-blue-600 mt-1">
              Row {currentRow} of {totalRows}
            </div>
          </div>
        )}
      </div>

      {/* Processing Details */}
      <div className="bg-white border rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">Import Details</h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Started at:</span>
            <span className="text-sm text-gray-900">{new Date().toLocaleTimeString()}</span>
          </div>
          {isComplete && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Completed at:</span>
              <span className="text-sm text-gray-900">{new Date().toLocaleTimeString()}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Total rows to process:</span>
            <span className="text-sm text-gray-900">{totalRows}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Success rate:</span>
            <span className="text-sm text-gray-900">
              {processedRows > 0 ? Math.round((successfulRows / processedRows) * 100) : 0}%
            </span>
          </div>
        </div>
      </div>

      {/* Completion Message */}
      {isComplete && (
        <div className={`border rounded-lg p-6 ${
          status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h4 className={`font-medium ${
                status === 'completed' ? 'text-green-800' : 'text-red-800'
              }`}>
                {status === 'completed' ? 'Import Completed!' : 'Import Cancelled'}
              </h4>
              <p className={`text-sm mt-1 ${
                status === 'completed' ? 'text-green-700' : 'text-red-700'
              }`}>
                {status === 'completed' 
                  ? `Successfully imported ${successfulRows} assets. ${failedRows > 0 ? `${failedRows} rows failed.` : ''}`
                  : 'The import operation was cancelled and may be incomplete.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Warning about page navigation */}
      {!isComplete && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-700">
            ⚠️ <strong>Do not close this page or navigate away</strong> while the import is in progress. 
            This may result in incomplete data import.
          </p>
        </div>
      )}
    </div>
  );
};

export default ImportProgress; 